import type { InventoryItem } from '@app/types/shop-bff/types';
import Box from '@redbubble/design-system/src/Box';
import { FieldArray } from '@redbubble/design-system/src/Form';
import RadioSelectorField from '@redbubble/design-system/src/Form/RadioSelectorField';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import Stack from '@redbubble/design-system/src/Stack';
import Text from '@redbubble/design-system/src/Text';
import { type ReactNode, useMemo } from 'react';
import { LoadingBoundary } from '../LoadingBoundary';
import styles from './ColorFieldConfigurator.module.scss';
import { compareDisplayOrder } from './configuratorFormUtils';
import {
  BodySleeveSwatch,
  ColorWithBackingSwatch,
  SingleColorSwatch,
} from './OptionSwatch';
import {
  ConfiguratorOption,
  getTextureFromPublicAssets,
  getTranslatedString,
} from './utils';

export type ColorFieldConfiguratorProps = {
  locale: string;
  option: ConfiguratorOption;
  currentValue: string;
  filterValues: Array<{
    value: ConfiguratorOption['values'][number];
    inventoryItem: InventoryItem;
  }>;
  errors: Record<string, string>;
};

type FormikForm = { setFieldValue: (field: string, value: unknown) => void };

const ColorFieldSkeleton = () => {
  return (
    <Box marginLeft="xs" marginRight="xs" marginBottom="l">
      <Stack spacing="xs">
        <Skeleton height={26} width={130} animated roundedCorners />
        <Skeleton height={44} animated roundedCorners />
      </Stack>
    </Box>
  );
};

export const ColorFieldConfigurator: React.FC<ColorFieldConfiguratorProps> = ({
  locale,
  option,
  currentValue,
  filterValues,
}) => {
  const label = getTranslatedString(option.publicName, option.key, locale);

  const sortedValues = useMemo(
    () =>
      filterValues
        .map(({ value }) => value)
        .sort((a, b) =>
          compareDisplayOrder(
            { displayOrder: a.publicDisplayOrder },
            { displayOrder: b.publicDisplayOrder }
          )
        ),
    [filterValues]
  );

  const currentOptionValue = sortedValues.find(
    (value) => value.equivalentValue === currentValue
  );
  const valueSubtitle = currentOptionValue
    ? getTranslatedString(
        currentOptionValue.publicName,
        currentOptionValue.equivalentValue,
        locale
      )
    : undefined;

  return (
    <LoadingBoundary loadingComponent={<ColorFieldSkeleton />}>
      <Box
        paddingLeft={0.5}
        paddingRight={0.5}
        marginBottom={1.25}
        data-testid="form-field-color"
      >
        <div role="radiogroup" aria-label={label}>
          <Text type="display6" element="div" marginBottom="xs">
            {label}
            {valueSubtitle && (
              <Text
                marginLeft="xs"
                type="body"
                display="inline"
                data-testid="ds-field-label-subtitle"
              >
                {valueSubtitle}
              </Text>
            )}
          </Text>

          <FieldArray
            name={option.key}
            render={({ form }: { form: FormikForm }) => (
              <span className={styles.group}>
                {sortedValues.map((value) => {
                  const selected = value.equivalentValue === currentValue;
                  const config = value.config;
                  const optionLabel = getTranslatedString(
                    value.publicName,
                    value.equivalentValue,
                    locale
                  );

                  let swatch: ReactNode = null;
                  switch (config?.kind) {
                    case 'body-sleeve-color':
                      swatch = (
                        <BodySleeveSwatch
                          bodyHexColor={config.bodyColor?.hexColor}
                          sleeveHexColor={config.sleeveColor?.hexColor}
                          selected={selected}
                        />
                      );
                      break;
                    case 'color-with-backing':
                      swatch = (
                        <ColorWithBackingSwatch
                          productHexColor={config.productColor?.hexColor}
                          backingHexColor={config.backingColor?.hexColor}
                          selected={selected}
                        />
                      );
                      break;
                    case 'single-color':
                      swatch = (
                        <SingleColorSwatch
                          hexColor={config.color?.hexColor ?? ''}
                          imageUrl={
                            config.color?.texture
                              ? getTextureFromPublicAssets(config.color.texture)
                              : undefined
                          }
                          selected={selected}
                        />
                      );
                      break;
                    default:
                      return (
                        <RadioSelectorField
                          key={`${option.key}_${value.equivalentValue}`}
                          label={label}
                          options={[
                            {
                              value: value.equivalentValue,
                              label: optionLabel,
                            },
                          ]}
                          field={{
                            name: option.key,
                            value: currentValue,
                            onBlur: () => {},
                          }}
                          form={form}
                        />
                      );
                  }

                  return (
                    <label
                      key={`${option.key}_${value.equivalentValue}`}
                      className={styles.color}
                      title={optionLabel}
                    >
                      <input
                        type="radio"
                        className={styles.input}
                        name={option.key}
                        value={value.equivalentValue}
                        checked={selected}
                        aria-label={`${optionLabel}, ${label}`}
                        onChange={() =>
                          form.setFieldValue(option.key, value.equivalentValue)
                        }
                      />
                      {swatch}
                    </label>
                  );
                })}
              </span>
            )}
          />
        </div>
      </Box>
    </LoadingBoundary>
  );
};
